import React, { useState } from 'react';
import { Phone, Mail, MapPin, CheckCircle, Send, Clock } from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import SEO from '../components/SEO';
import { useContent } from '../hooks/useContent';

const ContactPage = () => {
  const { content: heroImages } = useContent('heroImages');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Failed to send message. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Contact Us"
        description="Contact Lawman Commercials for expert MAN truck services in Leicester. Call 01530 231 235 or email sales@lawmancommercials.com. Mon-Fri 8am-5:30pm, Sat 8am-12pm. Professional truck sales, hire & repairs."
        keywords="contact Lawman Commercials, MAN truck services Leicester, truck repair contact, commercial vehicle services contact"
        canonical="/contact"
      />
      
      {/* Header */}
      <section 
        className="bg-black text-white pt-48 pb-40 -mt-24 relative"
        style={{
          backgroundImage: `url('${heroImages?.contactPage || 'https://customer-assets.emergentagent.com/job_be970307-c621-4433-83c7-b7d829e263c4/artifacts/f4cn9fsi_WhatsApp%20Image%202025-09-29%20at%2011.41.42.jpeg'}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          minHeight: '70vh'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-inter">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto font-inter mb-8">
            Get in touch with our expert team for all your commercial vehicle needs
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded font-bold transition-colors shadow-lg"
            >
              Get in Touch
            </button>
            <button 
              onClick={() => document.getElementById('find-us')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-3 rounded font-bold transition-colors shadow-lg"
            >
              How to Find Us
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-bold text-black mb-8 font-inter">Get In Touch</h2>
            
            <div className="space-y-8">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 rounded-full p-3">
                  <MapPin className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Address</h3>
                  <p className="text-gray-600 mb-2">Visit our showroom</p>
                  <p className="text-black">31 Merylees Industrial Est</p>
                  <p className="text-black">Leeside, Desford, Leicestershire</p>
                  <p className="text-black">LE9 9FS</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 rounded-full p-3">
                  <Phone className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Phone</h3>
                  <p className="text-gray-600 mb-2">Speak directly with our team</p>
                  <div className="space-y-1">
                    <div>
                      <a href="tel:01530231235" className="text-xl font-bold text-orange-600 hover:text-orange-700">01530 231 235</a>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Sales Manager Jake: </span>
                      <a href="tel:07388993097" className="text-lg text-orange-600 hover:text-orange-700">07388 993 097</a>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">General Manager John: </span>
                      <a href="tel:07718738785" className="text-lg text-orange-600 hover:text-orange-700">07718 738 785</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 rounded-full p-3">
                  <Mail className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Email</h3>
                  <p className="text-gray-600 mb-2">Send us your enquiries</p>
                  <div className="space-y-1">
                    <div>
                      <span className="text-sm text-gray-500">Sales: </span>
                      <a href="mailto:Sales@lawmancommercials.com" className="text-lg text-orange-600 hover:text-orange-700">Sales@lawmancommercials.com</a>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Accounts: </span>
                      <a href="mailto:Nancie@lawmancommercials.com" className="text-lg text-orange-600 hover:text-orange-700">Nancie@lawmancommercials.com</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 rounded p-3">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Opening Hours</h3>
                  <p className="text-gray-600 mb-2">Visit us during these times</p>
                  <div className="space-y-1">
                    <p className="text-black"><span className="font-semibold">Mon-Fri:</span> 8:00am – 5:30pm</p>
                    <p className="text-black"><span className="font-semibold">Saturday:</span> 8:00am – 12:00pm</p>
                    <p className="text-black"><span className="font-semibold">Sunday:</span> Closed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 rounded p-6">
              <h3 className="font-bold text-lg mb-4 uppercase">Why Choose Lawman Commercials?</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span>30+ years MAN truck expertise</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span>Quality MAN trucks with bespoke builds</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span>Export to 30+ countries worldwide</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span>MANCAT3 diagnostic capabilities</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span>Full inspection and up to 12 months coverage</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div id="contact-form" className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-black mb-6 font-inter">Send us a Message</h2>
            
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded p-6 text-center">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-green-800 mb-2">Message Sent Successfully!</h3>
                <p className="text-green-700">Thank you for contacting us. We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      placeholder="01234 567890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      placeholder="Truck Inquiry"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter resize-none"
                    placeholder="Tell us about your requirements..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-8 py-4 rounded font-bold text-lg transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Google Map Section */}
      <section id="find-us" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-black mb-4 font-inter">Find Us</h2>
            <p className="text-lg text-gray-600 font-inter">
              Visit our facility at Merrylees Industrial Estate
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <iframe
              src="https://maps.google.com/maps?q=31+Merrylees+Industrial+Estate,+Leeside,+Desford,+Leicester+LE9+9FS,+UK&t=m&z=17&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Lawman Commercials Location"
              className="w-full"
            ></iframe>
            
            <div className="p-6 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <h4 className="font-bold text-black mb-2">Address</h4>
                  <p className="text-gray-600 text-sm">
                    31 Merrylees Industrial Estate<br/>
                    Leeside, Desford, Leicester<br/>
                    LE9 9FS
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-2">Phone</h4>
                  <p className="text-gray-600 text-sm">
                    <a href="tel:01530231235" className="text-orange-600 hover:text-orange-700">Office: 01530 231 235</a><br/>
                    <a href="tel:07718738785" className="text-orange-600 hover:text-orange-700">General Manager John: 07718 738 785</a><br/>
                    <a href="tel:07388993097" className="text-orange-600 hover:text-orange-700">Sales Manager Jake: 07388 993 097</a>
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-2">Hours</h4>
                  <p className="text-gray-600 text-sm">
                    Mon-Fri: 8:00am – 5:30pm<br/>
                    Sat: 8:00am – 12:00pm<br/>
                    Sun: Closed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6 font-inter">
            Ready to Get in Touch?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Contact us today for all your MAN truck requirements
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <a 
              href="tel:01530231235"
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-3 shadow-lg"
            >
              <Phone className="h-6 w-6" />
              Call Now: 01530 231 235
            </a>
            
            <a 
              href="mailto:sales@lawmancommercials.com"
              className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-3"
            >
              <Mail className="h-6 w-6" />
              Email: sales@lawmancommercials.com
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <NewsletterSignup />
    </div>
  );
};

export default ContactPage;